import { useState, useEffect } from "react";
import {
  deleteQuoteRequest,
  getQuoteRequests,
  updateQuoteRequest,
} from "../../../services/api";
import { toast, ToastContainer } from "react-toastify";
import { QuoteRequestsTable } from "../../../components/dashboard/quote-request/quote-requests-table";

interface GlamPackage {
  id: string;
  title: string;
  price: number;
  description: string;
  image_path: string;
  created_at: string;
  updated_at: string;
}

interface QuoteRequest {
  id: string;
  name: string;
  email_address: string;
  phone_number: string;
  glam_package_id: string;
  glam_spot: string;
  number_of_faces: string;
  booking_start_date: string;
  booking_end_date: string;
  booking_start_date_time: string;
  booking_end_date_time: string;
  home_address: string | null;
  landmark: string | null;
  additional_info: string | null;
  google_calendar_link: string | null;
  booking_name: string;
  created_at: string;
  updated_at: string;
  glam_package: GlamPackage;
}

interface PaginatedResponse {
  current_page: number;
  data: QuoteRequest[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

interface ApiResponse {
  message: string;
  data: PaginatedResponse;
}

export default function QuotesPage() {
  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        setIsLoading(true);
        const response = await getQuoteRequests();
        const apiResponse = response.data as ApiResponse;

        // Handle the nested data structure
        const quotesData = apiResponse.data.data || [];
        setQuotes(quotesData);

        // Set pagination info
        setPagination({
          currentPage: apiResponse.data.current_page,
          totalPages: apiResponse.data.last_page,
          totalItems: apiResponse.data.total,
        });
      } catch (error) {
        console.error("Failed to fetch quote requests:", error);
        toast.error("Failed to load quote requests");
        // Initialize with empty array to prevent errors
        setQuotes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchQuotes();
  }, []);

  const handleDeleteQuote = async (id: string) => {
    try {
      await deleteQuoteRequest(id);
      setQuotes(quotes.filter((quote) => quote.id !== id));
      toast.success("Quote request deleted successfully");
    } catch (error) {
      console.error("Failed to delete quote request:", error);
      toast.error("Failed to delete quote request");
    }
  };

  const handleUpdateQuote = async (id: string, data: Partial<QuoteRequest>) => {
    try {
      await updateQuoteRequest(id, data);
      setQuotes(
        quotes.map((quote) => (quote.id === id ? { ...quote, ...data } : quote))
      );
      toast.success("Quote request updated successfully");
    } catch (error) {
      console.error("Failed to update quote request:", error);
      toast.error("Failed to update quote request");
    }
  };

  return (
    <div className="p-8 space-y-6">
      <ToastContainer />
      <div>
        <h1 className="text-2xl font-serif font-medium">Quote Requests</h1>
        <p className="text-sm text-muted-foreground">
          Manage user requests for makeup session pricing.
        </p>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <>
          <QuoteRequestsTable
            quotes={quotes}
            onDeleteQuote={handleDeleteQuote}
            onUpdateQuote={handleUpdateQuote}
          />

          {pagination.totalItems > 0 && (
            <div className="flex justify-between items-center mt-4 text-sm text-gray-500">
              <div>
                Showing {quotes.length} of {pagination.totalItems} quote
                requests
              </div>
              {/* Pagination controls could be added here if needed */}
            </div>
          )}
        </>
      )}
    </div>
  );
}
